import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl, proofUrl, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function Approval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [feedback, setFeedback] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get(`/approvals/${token}`)).data); }
    catch (err) { setData(null); toast.error(apiError(err.response?.data?.detail) || "This approval link is invalid"); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/approvals/${token}/approve`); toast.success("Approved — thank you!"); await load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not approve"); }
    finally { setBusy(false); }
  };
  const sendChanges = async () => {
    if (!feedback.trim()) { toast.error("Please tell us what to change"); return; }
    setBusy(true);
    try { await api.post(`/approvals/${token}/request-changes`, { feedback: feedback.trim() }); toast.success("Sent — we'll be in touch"); setShowChanges(false); await load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" />Loading approval…</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400" data-testid="approval-invalid">This approval link is invalid or has expired.</div>;

  const s = data.sheet || {};
  const approved = data.status === "approved";
  const changes = data.status === "changes_requested";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="approval-page">
      <p className="text-xs font-mono-p uppercase tracking-widest text-[#ff6a13]">Order {data.order_number}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">Approve your gang sheet</h1>

      <div className="mt-6 bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
        <div className="checker rounded-xl overflow-hidden flex items-center justify-center min-h-[220px]">
          {s.preview_url ? <img src={proofUrl(s.preview_url)} alt={s.name} className="max-w-full max-h-[420px] object-contain" data-testid="approval-preview" /> : <span className="text-zinc-500">No preview</span>}
        </div>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-2">
          <div>
            <p className="text-white font-semibold">{s.name}</p>
            <p className="text-sm text-zinc-400 font-mono-p">{s.sheet_width}" × {s.sheet_height}" · ×{s.quantity}</p>
            {s.notes && <p className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">Note: {s.notes}</p>}
          </div>
          <p className="font-display text-2xl font-black text-white font-mono-p">${((s.unit_price || 0) * (s.quantity || 1)).toFixed(2)}</p>
        </div>
        <div className="border-t border-zinc-800 mt-4 pt-4 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-zinc-400">Updated order total</span><span className="text-white font-mono-p">${(data.order_total || 0).toFixed(2)}</span></div>
          {data.requires_deposit && <div className="flex justify-between"><span className="text-zinc-400">Balance due (collected by staff)</span><span className="text-white font-mono-p">${(data.balance_due || 0).toFixed(2)}</span></div>}
        </div>
      </div>

      {approved ? (
        <div className="mt-6 rounded-xl border border-[#10b981]/40 bg-[#10b981]/10 p-4 flex items-center gap-3 text-[#10b981]" data-testid="approval-approved">
          <CheckCircle2 /> <span className="font-semibold">Approved — we're printing it. Thank you!</span>
        </div>
      ) : (
        <>
          {changes && (
            <div className="mt-6 rounded-xl border border-[#ffd600]/40 bg-[#ffd600]/10 p-4 flex items-start gap-3 text-[#ffd600]" data-testid="approval-changes">
              <AlertTriangle className="mt-0.5" /><div><p className="font-semibold">Changes requested</p>{data.customer_feedback && <p className="text-sm mt-1">"{data.customer_feedback}"</p>}<p className="text-sm mt-1 text-zinc-400">You can still approve below once you're happy.</p></div>
            </div>
          )}
          {!showChanges ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={approve} disabled={busy} data-testid="approval-approve-btn" className="bg-[#10b981] hover:bg-[#0e9e70] disabled:opacity-60 text-white rounded-full h-12 px-8 font-semibold">Approve &amp; print</Button>
              <Button onClick={() => setShowChanges(true)} variant="outline" data-testid="approval-changes-btn" className="rounded-full h-12 px-6 border-zinc-700 bg-transparent text-white hover:bg-zinc-800">Request changes</Button>
            </div>
          ) : (
            <div className="mt-6 bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
              <p className="text-sm text-zinc-300 mb-2">What would you like changed?</p>
              <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} data-testid="approval-feedback" placeholder="e.g. make the logo bigger, swap the third design…" className="bg-[#1e222d] border-zinc-700 text-white" />
              <div className="flex gap-3 mt-3">
                <Button onClick={sendChanges} disabled={busy} data-testid="approval-send-changes" className="bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full font-semibold">Send request</Button>
                <Button onClick={() => setShowChanges(false)} variant="outline" className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800">Cancel</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
